import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus } from 'lucide-react';
import {
  type AddTimelineVendorTimesResult,
  type EventBooking,
  type VendorTimeKind,
} from '@peles/shared';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useAddTimelineVendorTimes, useTimelines } from '../../hooks/useEventOps';
import { extractApiErrorMessage } from '../../utils/apiError';

/** What an apply did, in a sentence. */
export function describeTimelineAdd(
  result: AddTimelineVendorTimesResult,
  timeline: string,
): string {
  const parts: string[] = [];
  if (result.added > 0) {
    parts.push(`Added ${result.added === 1 ? '1 time' : `${result.added} times`} to ${timeline}.`);
  }
  if (result.alreadyOn > 0) {
    parts.push(
      result.added > 0
        ? `${result.alreadyOn} ${result.alreadyOn === 1 ? 'was' : 'were'} on it already.`
        : `Already on ${timeline}.`,
    );
  }
  if (result.unavailable > 0 && parts.length === 0) {
    parts.push('Nothing to add — save the times first.');
  }
  return parts.join(' ');
}

/**
 * "Add to timeline" beside a booking's day-of times: puts its saved arrival and
 * load-in on the event's timeline (the same request the Timeline screen's
 * "Vendor times" makes, so nothing goes on twice). One timeline — straight to
 * it; several — pick one; none — says where to make one. Only times already
 * saved are offered, since the server reads them from the booking.
 */
export function BookingTimelineAction({
  booking,
  eventId,
}: {
  booking: EventBooking;
  eventId: string;
}) {
  const kinds: VendorTimeKind[] = [
    ...(booking.metadata.arrivalTime ? (['arrival'] as const) : []),
    ...(booking.metadata.loadInTime ? (['loadIn'] as const) : []),
  ];
  const { data: allTimelines, isLoading } = useTimelines(eventId, { enabled: kinds.length > 0 });
  const add = useAddTimelineVendorTimes();
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  if (kinds.length === 0) return null;
  const timelines = (allTimelines ?? []).filter((t) => !t.isTemplate && !t.isArchived);
  const nameOf = (id: string) =>
    timelines.find((t) => t.id === id)?.title?.trim() || 'the timeline';

  if (!isLoading && timelines.length === 0) {
    return (
      <p className="sm:col-span-2 text-xs text-gray-500" data-testid="booking-timeline-none">
        This event has no timeline yet.{' '}
        <Link
          to={`/events/${eventId}/timeline`}
          className="text-primary-600 hover:text-primary-700 underline"
        >
          Make one on the Timeline tab
        </Link>{' '}
        to put these times on it.
      </p>
    );
  }

  const addTo = async (timelineId: string) => {
    setMessage(null);
    try {
      const result = await add.mutateAsync({
        timelineId,
        items: kinds.map((kind) => ({ linkId: booking.linkId, kind })),
      });
      setMessage({ text: describeTimelineAdd(result, nameOf(timelineId)), error: false });
      setPicking(false);
    } catch (err) {
      setMessage({
        text: extractApiErrorMessage(err, 'The times could not be added to the timeline.'),
        error: true,
      });
    }
  };

  const start = () => {
    if (timelines.length === 1) void addTo(timelines[0].id);
    else setPicking(true);
  };

  const buttonClass =
    'inline-flex items-center gap-1.5 min-h-11 sm:min-h-8 px-3 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50';

  return (
    // An Enter in the picker opens it; it must not submit the details form round it.
    <div
      className="sm:col-span-2 space-y-2"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) e.preventDefault();
      }}
    >
      {picking ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex-1 min-w-0">
            <SearchableListSelect
              items={timelines}
              value={chosen}
              onChange={(v) => setChosen(typeof v === 'string' && v ? v : null)}
              getValue={(t) => t.id}
              getLabel={(t) => t.title?.trim() || 'Untitled timeline'}
              placeholder="Choose a timeline"
              ariaLabel="Timeline"
              clearable={false}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!chosen || add.isPending}
              onClick={() => chosen && void addTo(chosen)}
              className={buttonClass}
            >
              {add.isPending ? 'Adding…' : 'Add'}
            </button>
            <button type="button" onClick={() => setPicking(false)} className={buttonClass}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={start}
          disabled={isLoading || add.isPending}
          className={buttonClass}
        >
          <CalendarPlus size={15} aria-hidden="true" />
          {add.isPending ? 'Adding…' : 'Add to timeline'}
        </button>
      )}
      {message && (
        <p
          role={message.error ? 'alert' : 'status'}
          className={`text-xs ${message.error ? 'text-red-700' : 'text-gray-600'}`}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}
